import { useEffect, useState, type ReactNode } from "react"

import { useAuth } from "@clerk/react"
import { useNavigate, useRouterState } from "@tanstack/react-router"

import {
  FoundationCardSkeleton,
  FoundationLayoutSkeleton,
  PublicSidebarSectionSkeleton,
} from "@/components/app/public-loading-skeleton"

interface ClerkRouteGuardProps {
  children: ReactNode
  loadingFallback?: ReactNode
  title?: string
}

export function ClerkRouteGuard({
  children,
  loadingFallback,
  title = "Loading...",
}: ClerkRouteGuardProps) {
  const { isLoaded, userId } = useAuth()
  const navigate = useNavigate()
  const currentHref = useRouterState({
    select: (state) => state.location.href || "/account",
  })
  const [redirectUrl] = useState(currentHref)

  useEffect(() => {
    if (!isLoaded || userId) {
      return
    }

    void navigate({
      replace: true,
      search: { redirect_url: redirectUrl },
      to: "/sign-in",
    })
  }, [isLoaded, navigate, redirectUrl, userId])

  if (!isLoaded) {
    if (loadingFallback) {
      return <>{loadingFallback}</>
    }

    return (
      <FoundationLayoutSkeleton
        srTitle={title}
        testId="foundation-route-skeleton"
      >
        <section className="grid gap-5 xl:grid-cols-[minmax(0,1.15fr)_20rem]">
          <FoundationCardSkeleton rows={4} />
          <div className="grid gap-5">
            <PublicSidebarSectionSkeleton rows={3} />
          </div>
        </section>
      </FoundationLayoutSkeleton>
    )
  }

  if (!userId) {
    return null
  }

  return <>{children}</>
}
